<template>
  <div class="astro-wuerfel-page">
    <Navigation />
    <main class="container">
      <header class="page-header">
        <h1 class="page-title">{{ $t('astroDice.pageTitle') }}</h1>
        
        <p class="source">{{ $t('astroDice.source') }} <a href="https://www.astro.com/astrowiki/de/Astrow%C3%BCrfel" target="_blank" rel="noopener">Astro‑Würfel Astrowiki</a></p>
      </header>

      

      <section class="roller">
        <div class="dice-controls">
          <button class="chip" :class="{ active: showPlanet }" @click="showPlanet = !showPlanet">{{ $t('astroDice.planet') }}</button>
          <button class="chip" :class="{ active: showHouse }" @click="showHouse = !showHouse">{{ $t('astroDice.house') }}</button>
          <button class="chip" :class="{ active: showSign }" @click="showSign = !showSign">{{ $t('astroDice.sign') }}</button>
        </div>
        <div class="dice-grid">
          <div class="die-card" v-if="showPlanet">
            <div class="die-title">{{ $t('astroDice.planet') }}</div>
            <div class="cube-scene">
              <div class="cube" :style="{ transform: planetTransform }">
                <div class="face front">{{ selected.planet.symbol }}</div>
                <div class="face back">{{ selected.planet.symbol }}</div>
                <div class="face right">{{ selected.planet.symbol }}</div>
                <div class="face left">{{ selected.planet.symbol }}</div>
                <div class="face top">{{ selected.planet.symbol }}</div>
                <div class="face bottom">{{ selected.planet.symbol }}</div>
              </div>
            </div>
            <div class="die-face">{{ selected.planet.symbol }} <span class="die-name">{{ selected.planet.name }}</span></div>
          </div>
          <div class="die-card" v-if="showHouse">
            <div class="die-title">{{ $t('astroDice.house') }}</div>
            <div class="cube-scene">
              <div class="cube" :style="{ transform: houseTransform }">
                <div class="face front">{{ selected.house.symbol }}</div>
                <div class="face back">{{ selected.house.symbol }}</div>
                <div class="face right">{{ selected.house.symbol }}</div>
                <div class="face left">{{ selected.house.symbol }}</div>
                <div class="face top">{{ selected.house.symbol }}</div>
                <div class="face bottom">{{ selected.house.symbol }}</div>
              </div>
            </div>
            <div class="die-face">{{ selected.house.symbol }} <span class="die-name">{{ selected.house.name }}</span></div>
          </div>
          <div class="die-card" v-if="showSign">
            <div class="die-title">{{ $t('astroDice.sign') }}</div>
            <div class="cube-scene">
              <div class="cube" :style="{ transform: signTransform }">
                <div class="face front">{{ selected.sign.symbol }}</div>
                <div class="face back">{{ selected.sign.symbol }}</div>
                <div class="face right">{{ selected.sign.symbol }}</div>
                <div class="face left">{{ selected.sign.symbol }}</div>
                <div class="face top">{{ selected.sign.symbol }}</div>
                <div class="face bottom">{{ selected.sign.symbol }}</div>
              </div>
            </div>
            <div class="die-face">{{ selected.sign.symbol }} <span class="die-name">{{ selected.sign.name }}</span></div>
          </div>
        </div>

        <div class="actions">
          <button class="btn primary" @click="rollAll">{{ $t('astroDice.roll') }}</button>
          <button class="btn ghost" @click="reset">{{ $t('astroDice.reset') }}</button>
        </div>

        <div class="options">
          <label class="toggle">
            <input type="checkbox" v-model="showInterpretation">
            <span class="toggle-track"></span>
            <span class="toggle-label">{{ $t('astroDice.showInterpretation') }}</span>
          </label>
        </div>
      </section>

      <section class="interpretation" v-if="showInterpretation && (showPlanet || showHouse || showSign)">
        <h2>{{ $t('astroDice.interpretationTitle') }}</h2>
        <p class="hint">{{ $t('astroDice.interpretationHint') }}</p>
        <div class="cards">
          <div class="card" v-if="showPlanet">
            <h3>{{ $t('astroDice.planet') }}</h3>
            <p class="title">{{ selected.planet.name }}</p>
            <ul>
              <li v-for="k in selected.planet.keywords" :key="k">{{ k }}</li>
            </ul>
          </div>
          <div class="card" v-if="showHouse">
            <h3>{{ $t('astroDice.house') }}</h3>
            <p class="title">{{ selected.house.name }}</p>
            <ul>
              <li v-for="k in selected.house.keywords" :key="k">{{ k }}</li>
            </ul>
          </div>
          <div class="card" v-if="showSign">
            <h3>{{ $t('astroDice.sign') }}</h3>
            <p class="title">{{ selected.sign.name }}</p>
            <ul>
              <li v-for="k in selected.sign.keywords" :key="k">{{ k }}</li>
            </ul>
          </div>
        </div>

        <div class="compose">
          <h3>{{ $t('astroDice.yourReading') }}</h3>
          <textarea v-model="note" :placeholder="$t('astroDice.composePlaceholder')"></textarea>
          <div class="compose-actions">
            <button class="btn" @click="copyNote">{{ $t('astroDice.copy') }}</button>
            <span class="copied" v-if="copied">{{ $t('astroDice.copied') }}</span>
          </div>
        </div>
      </section>

      <section class="howto">
        <h3 class="hook">{{ $t('astroDice.howToTitle') }}</h3>
        <ol class="steps">
          <li>{{ $t('astroDice.step1') }}</li>
          <li>{{ $t('astroDice.step2') }}</li>
          <li>{{ $t('astroDice.step3') }}</li>
          <li>{{ $t('astroDice.step4') }}</li>
        </ol>
      </section>
    </main>

    <LegalFooter />
  </div>
</template>

<script>
import Navigation from '../sections/Navigation.vue'
import LegalFooter from '../legal/LegalFooter.vue'

const PLANETS = [
  { name: 'Sonne', symbol: '☉', keywords: ['Identität', 'Selbstbewusstsein', 'Lebensziel', 'kreativer Ausdruck'] },
  { name: 'Mond', symbol: '☾', keywords: ['emotionale Bedürfnisse', 'Bindung', 'Sicherheit', 'Gewohnheiten'] },
  { name: 'Merkur', symbol: '☿', keywords: ['Denken', 'Wahrnehmung', 'Kommunikation', 'Lernstil'] },
  { name: 'Venus', symbol: '♀', keywords: ['Werte', 'Beziehungsstil', 'Selbstwert', 'Genuss'] },
  { name: 'Mars', symbol: '♂', keywords: ['Wille', 'Durchsetzung', 'Energiehaushalt', 'Konfliktstil'] },
  { name: 'Jupiter', symbol: '♃', keywords: ['Sinn', 'Glaube', 'Expansion', 'Vertrauen'] },
  { name: 'Saturn', symbol: '♄', keywords: ['Grenze', 'Verantwortung', 'Struktur', 'Reife', 'Angstbewältigung'] },
  { name: 'Uranus', symbol: '♅', keywords: ['Autonomie', 'Befreiung', 'Originalität', 'Musterbruch'] },
  { name: 'Neptun', symbol: '♆', keywords: ['Sehnsucht', 'Empathie', 'Ideal', 'Grenzenauflösung'] },
  { name: 'Pluto', symbol: '♇', keywords: ['Macht', 'Wandlung', 'Intensität', 'Schattenarbeit'] },
  { name: 'Nordknoten', symbol: '☊', keywords: ['Entwicklungsrichtung', 'Lernaufgabe', 'Wachstum'] },
  { name: 'Südknoten', symbol: '☋', keywords: ['Komfortzone', 'Prägung', 'Vergangenheit', 'Loslassen'] },
]

const HOUSE_KEYWORDS = [
  ['Auftreten', 'Persona', 'Selbstbild'],
  ['Ressourcen', 'Selbstwert', 'Besitz', 'Körperempfinden'],
  ['Denken', 'Austausch', 'Umfeld', 'Geschwister'],
  ['Herkunft', 'emotionale Basis', 'Zuhause', 'innere Sicherheit'],
  ['Kreativität', 'Spiel', 'Selbstentfaltung', 'Romantik'],
  ['Alltag', 'Gesundheit', 'Arbeitsethik', 'Dienst'],
  ['Partnerschaft', 'Projektion', 'Gleichgewicht', 'Verträge'],
  ['Intimität', 'Bindung', 'Transformation', 'Grenzen teilen'],
  ['Sinnsuche', 'Weltbild', 'Bildung', 'Reisen'],
  ['Berufung', 'Status', 'Verantwortung', 'Autorität'],
  ['Freundschaft', 'Netzwerke', 'Vision', 'soziale Rolle'],
  ['Rückzug', 'Unbewusstes', 'Hingabe', 'Heilung']
]

const HOUSES = Array.from({ length: 12 }, (_, i) => ({
  name: `${i + 1}. Haus`,
  symbol: `H${i + 1}`,
  keywords: HOUSE_KEYWORDS[i]
}))

const SIGNS = [
  { name: 'Widder', symbol: '♈', keywords: ['Initiative', 'Mut', 'Impulsivität', 'Pioniergeist'] },
  { name: 'Stier', symbol: '♉', keywords: ['Beständigkeit', 'Sinnlichkeit', 'Genuss', 'Werte'] },
  { name: 'Zwillinge', symbol: '♊', keywords: ['Neugier', 'Vielseitigkeit', 'Kommunikation', 'Lernen'] },
  { name: 'Krebs', symbol: '♋', keywords: ['Fürsorge', 'Schutz', 'Emotionalität', 'Zugehörigkeit'] },
  { name: 'Löwe', symbol: '♌', keywords: ['Selbstvertrauen', 'Bühne', 'Kreativität', 'Herz'] },
  { name: 'Jungfrau', symbol: '♍', keywords: ['Analyse', 'Ordnung', 'Optimierung', 'Dienst'] },
  { name: 'Waage', symbol: '♎', keywords: ['Beziehung', 'Harmonie', 'Ästhetik', 'Vermittlung'] },
  { name: 'Skorpion', symbol: '♏', keywords: ['Tiefe', 'Kontrolle', 'Leidenschaft', 'Regeneration'] },
  { name: 'Schütze', symbol: '♐', keywords: ['Sinn', 'Weite', 'Optimismus', 'Überzeugungen'] },
  { name: 'Steinbock', symbol: '♑', keywords: ['Verantwortung', 'Disziplin', 'Zielorientierung', 'Struktur'] },
  { name: 'Wassermann', symbol: '♒', keywords: ['Freiheit', 'Individualität', 'Zukunft', 'Rebellion'] },
  { name: 'Fische', symbol: '♓', keywords: ['Empathie', 'Spiritualität', 'Mitgefühl', 'Grenzenlosigkeit'] },
]

export default {
  name: 'AstroWuerfel',
  components: { Navigation, LegalFooter },
  data() {
    return {
      selected: {
        planet: PLANETS[0],
        house: HOUSES[0],
        sign: SIGNS[0]
      },
      note: '',
      copied: false,
      showInterpretation: false,
      showPlanet: true,
      showHouse: true,
      showSign: true,
      planetRot: { x: 0, y: 0 },
      houseRot: { x: 0, y: 0 },
      signRot: { x: 0, y: 0 }
    }
  },
  mounted() {
    window.addEventListener('keydown', this.onKeyDown)
  },
  beforeUnmount() {
    window.removeEventListener('keydown', this.onKeyDown)
  },
  computed: {
    planetTransform() {
      return `rotateX(${this.planetRot.x}deg) rotateY(${this.planetRot.y}deg)`
    },
    houseTransform() {
      return `rotateX(${this.houseRot.x}deg) rotateY(${this.houseRot.y}deg)`
    },
    signTransform() {
      return `rotateX(${this.signRot.x}deg) rotateY(${this.signRot.y}deg)`
    }
  },
  methods: {
    onKeyDown(e) {
      if (e.code === 'Space') {
        // Nicht auslösen, wenn in Eingabefeldern geschrieben wird
        const ae = document.activeElement
        if (ae && (ae.tagName === 'INPUT' || ae.tagName === 'TEXTAREA' || ae.isContentEditable)) {
          return
        }
        e.preventDefault()
        this.rollAll()
      }
    },
    rollAll() {
      // animate and update only enabled dice
      if (this.showPlanet) {
        this.spinDie(this.planetRot)
        this.selected.planet = PLANETS[Math.floor(Math.random() * PLANETS.length)]
      }
      if (this.showHouse) {
        this.spinDie(this.houseRot)
        this.selected.house = HOUSES[Math.floor(Math.random() * HOUSES.length)]
      }
      if (this.showSign) {
        this.spinDie(this.signRot)
        this.selected.sign = SIGNS[Math.floor(Math.random() * SIGNS.length)]
      }
    },
    reset() {
      this.selected = { planet: PLANETS[0], house: HOUSES[0], sign: SIGNS[0] }
      this.note = ''
      this.copied = false
      this.planetRot = { x: 0, y: 0 }
      this.houseRot = { x: 0, y: 0 }
      this.signRot = { x: 0, y: 0 }
    },
    spinDie(rot) {
      const faces = [
        { x: 0, y: 0 },      // front
        { x: 0, y: 180 },    // back
        { x: 0, y: -90 },    // right
        { x: 0, y: 90 },     // left
        { x: 90, y: 0 },     // top
        { x: -90, y: 0 }     // bottom
      ]
      const target = faces[Math.floor(Math.random() * faces.length)]
      const turnsX = 360 * (2 + Math.floor(Math.random() * 3))
      const turnsY = 360 * (2 + Math.floor(Math.random() * 3))
      const currentX = ((rot.x % 360) + 360) % 360
      const currentY = ((rot.y % 360) + 360) % 360
      rot.x = rot.x + turnsX + (target.x - currentX)
      rot.y = rot.y + turnsY + (target.y - currentY)
    },
    async copyNote() {
      try {
        await navigator.clipboard.writeText(this.note)
        this.copied = true
        setTimeout(() => (this.copied = false), 1200)
      } catch (e) {
        console.error('Clipboard copy failed', e)
      }
    }
  }
}
</script>

<style scoped>
.astro-wuerfel-page {
  background: var(--bg-primary);
  color: var(--text-primary);
  min-height: 100vh;
}

.container {
  max-width: 1100px;
  margin: 90px auto 40px;
  padding: 0 20px;
}

.page-header h1 {
  margin: 0 0 8px 0;
}

.page-title {
  font-size: clamp(2rem, 4vw, 3rem);
  font-weight: 800;
  letter-spacing: 0.2px;
  background: var(--gradient-primary);
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
}

.lead { color: var(--text-secondary); margin: 0 0 8px 0; }
.source { color: var(--text-secondary); font-size: 0.9rem; }
.source a { color: var(--accent-primary); text-decoration: underline; }

.howto { margin: 24px 0 16px; }
.hook { margin: 0 0 8px 0; font-weight: 800; }
.howto h2 { margin: 0 0 8px 0; }
.steps { margin: 0 0 10px 18px; color: var(--text-secondary); }
.steps li { margin: 6px 0; }
.tips { background: var(--glass-bg); border: 1px solid var(--border-color); border-radius: 12px; padding: 12px; }
.tips h3 { margin: 0 0 6px 0; font-weight: 800; }
.tips ul { margin: 0 0 0 18px; color: var(--text-secondary); }
.tips li { margin: 4px 0; }

.dice-grid {
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 16px;
  margin: 20px 0;
}

.die-card {
  background: var(--glass-bg);
  border: 1px solid var(--border-color);
  border-radius: 16px;
  padding: 16px;
}

.cube-scene {
  width: 100px;
  height: 100px;
  margin: 6px auto 10px;
  perspective: 600px;
}

.cube {
  width: 100%;
  height: 100%;
  position: relative;
  transform-style: preserve-3d;
  transition: transform 1s cubic-bezier(0.2, 0.8, 0.2, 1);
}

.face {
  position: absolute;
  width: 100%;
  height: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 34px;
  font-weight: 800;
  color: #0f172a; /* dunkel für starke Lesbarkeit auf hell */
  background: #ffffff; /* helles, opakes Face */
  border: 1px solid #e5e7eb; /* dezente helle Kante */
  border-radius: 10px;
  box-shadow: 0 6px 18px rgba(0,0,0,0.18);
}

/* place faces */
.face.front { transform: translateZ(50px); }
.face.back { transform: rotateY(180deg) translateZ(50px); }
.face.right { transform: rotateY(90deg) translateZ(50px); }
.face.left { transform: rotateY(-90deg) translateZ(50px); }
.face.top { transform: rotateX(90deg) translateZ(50px); }
.face.bottom { transform: rotateX(-90deg) translateZ(50px); }

.die-title { color: var(--text-secondary); font-size: 0.9rem; margin-bottom: 6px; }
.die-face { font-size: 1.6rem; font-weight: 800; display: flex; align-items: center; gap: 10px; }
.die-name { font-weight: 700; font-size: 1rem; color: var(--text-secondary); }

.actions { display: flex; gap: 12px; margin: 10px 0 0; }
.btn { border: 1px solid var(--border-color); border-radius: 10px; padding: 10px 14px; cursor: pointer; }
.btn.primary { background: var(--gradient-primary); color: var(--text-primary); border: none; }
.btn.ghost { background: transparent; color: var(--text-primary); }

.dice-controls { display: flex; gap: 8px; flex-wrap: wrap; margin: 14px 0 4px; }
.chip { border: 1px solid var(--border-color); background: var(--glass-bg); color: var(--text-primary); border-radius: 9999px; padding: 6px 12px; cursor: pointer; font-weight: 600; }
.chip.active { background: var(--gradient-primary); color: var(--text-primary); border-color: transparent; }

.options { margin-top: 12px; }
.toggle { display: inline-flex; align-items: center; gap: 10px; cursor: pointer; user-select: none; }
.toggle input { display: none; }
.toggle-track { width: 44px; height: 24px; background: var(--glass-bg); border: 1px solid var(--border-color); border-radius: 9999px; position: relative; transition: all 0.2s ease; }
.toggle-track::after { content: ''; position: absolute; top: 2px; left: 2px; width: 20px; height: 20px; background: var(--text-primary); border-radius: 50%; transition: all 0.2s ease; }
.toggle input:checked + .toggle-track { background: var(--gradient-primary); border-color: transparent; }
.toggle input:checked + .toggle-track::after { left: 22px; background: var(--bg-primary); }
.toggle-label { color: var(--text-secondary); font-size: 0.95rem; }
.toggle-group { display: flex; gap: 14px; margin-top: 10px; flex-wrap: wrap; }

.interpretation { margin-top: 26px; }
.hint { color: var(--text-secondary); margin-bottom: 10px; }
.cards { display: grid; grid-template-columns: repeat(3, 1fr); gap: 16px; }
.card { background: var(--glass-bg); border: 1px solid var(--border-color); border-radius: 16px; padding: 16px; }
.card h3 { margin: 0 0 6px 0; }
.card .title { font-weight: 800; margin: 0 0 6px 0; }

.compose { margin-top: 16px; }
.compose textarea { width: 100%; min-height: 90px; border-radius: 12px; border: 1px solid var(--border-color); background: var(--bg-primary); color: var(--text-primary); padding: 10px 12px; }
.compose-actions { display: flex; align-items: center; gap: 10px; margin-top: 8px; }
.copied { color: var(--accent-green, #00ffb3); font-weight: 700; }

.page-tools { display: none; }

@media (max-width: 768px) {
  .dice-grid, .cards { grid-template-columns: 1fr; }
}
</style>


